import {
  memo,
  useMemo,
  type ComponentProps,
  type ComponentPropsWithoutRef,
  type ReactNode,
} from "react";
import { useTranslation } from "react-i18next";
import {
  TableVirtuoso,
  type ItemProps,
  type TableComponents,
  type TableProps,
} from "react-virtuoso";

import { AgentBadge } from "@/components/agent-badge";
import { NotificationIndicator } from "@/components/notification-indicator";
import { McpSessionIndicator } from "@/components/session-row";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import type { SessionNotificationState } from "@/hooks/use-notifications";
import { isMcpSession, type Session } from "@/lib/session";
import { cn } from "@/lib/utils";

const MINUTE_MS = 60_000;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;

function startOfLocalDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

export function formatLastTouched(iso: string, locale: string, yesterdayLabel: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "—";

  const now = new Date();
  const daysAgo = Math.round(
    (startOfLocalDay(now).getTime() - startOfLocalDay(date).getTime()) / DAY_MS,
  );
  const relative = new Intl.RelativeTimeFormat(locale, {
    numeric: "auto",
    style: "short",
  });

  if (daysAgo === 0) {
    const minutesAgo = Math.round((date.getTime() - now.getTime()) / MINUTE_MS);
    if (Math.abs(minutesAgo) < 60) return relative.format(minutesAgo, "minute");
    return relative.format(Math.round((date.getTime() - now.getTime()) / HOUR_MS), "hour");
  }
  if (daysAgo === 1) return yesterdayLabel;
  if (daysAgo > 1 && daysAgo < 7) return relative.format(-daysAgo, "day");

  const options: Intl.DateTimeFormatOptions = { month: "short", day: "numeric" };
  if (date.getFullYear() !== now.getFullYear()) options.year = "numeric";
  return date.toLocaleDateString(locale, options);
}

interface AgentConversationsTableProps {
  children: ReactNode;
  style?: ComponentPropsWithoutRef<"table">["style"];
  virtualized?: boolean;
}

function AgentConversationsTableHeaderRow() {
  const { t } = useTranslation();

  return (
    <TableRow className="grid w-full grid-cols-[minmax(0,1fr)_7rem] items-center gap-3 px-3 py-2 text-[11px] font-medium uppercase tracking-wide text-muted-foreground hover:bg-transparent">
      <TableHead scope="col" className="h-auto min-w-0 p-0 text-[11px] font-medium uppercase tracking-wide">
        {t("sidebar.conversations")}
      </TableHead>
      <TableHead scope="col" className="h-auto min-w-0 p-0 text-right text-[11px] font-medium uppercase tracking-wide">
        {t("sidebar.lastTouched")}
      </TableHead>
    </TableRow>
  );
}

function AgentConversationsTableHeader() {
  return (
    <TableHeader>
      <AgentConversationsTableHeaderRow />
    </TableHeader>
  );
}

/**
 * Table framing for the exact-agent conversations hub. The virtualized variant
 * below supplies this same table shell to TableVirtuoso.
 */
export function AgentConversationsTable({
  children,
  style,
  virtualized = false,
}: AgentConversationsTableProps) {
  const { t } = useTranslation();

  if (virtualized) {
    return (
      <Table aria-label={t("sidebar.conversations")} style={style}>
        {children}
      </Table>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <Table aria-label={t("sidebar.conversations")}>
        <AgentConversationsTableHeader />
        <TableBody>{children}</TableBody>
      </Table>
    </div>
  );
}

export interface AgentConversationsTableRowProps extends Omit<
  ComponentPropsWithoutRef<"tr">,
  "children" | "onSelect"
> {
  session: Session;
  agentName?: string;
  notifState: SessionNotificationState | null;
  isPending: boolean;
  hasDraft: boolean;
  isTaskSession: boolean;
  onSelect: (sessionId: string) => void;
}

/** A virtualized body row for an exact-agent conversation. */
export const AgentConversationsTableRow = memo(function AgentConversationsTableRow({
  session,
  agentName,
  notifState,
  isPending,
  hasDraft,
  isTaskSession,
  onSelect,
  className,
  ...tableRowProps
}: AgentConversationsTableRowProps) {
  const { t, i18n } = useTranslation();
  const mcpSession = isMcpSession(session);
  const lastTouched = formatLastTouched(
    session.last_active_at,
    i18n.language,
    t("sidebar.yesterday"),
  );

  return (
    <TableRow
      {...tableRowProps}
      role="row"
      className={cn(
        "grid w-full grid-cols-[minmax(0,1fr)_7rem] items-center gap-3 border-b px-3 py-0.5 last:border-b-0 hover:bg-transparent",
        className,
      )}
    >
      <TableCell role="cell" className="min-w-0 p-0">
        <button
          type="button"
          data-testid={`session-row-${session.id}`}
          onClick={() => onSelect(session.id)}
          className="group relative flex min-h-[36px] w-full items-center gap-2 overflow-hidden rounded-md px-2 py-1.5 text-left text-sm transition-colors hover:bg-muted"
        >
          <NotificationIndicator
            state={notifState}
            statusOverride={isPending ? "pending" : undefined}
            hasDraft={hasDraft}
            taskSession={isTaskSession}
            testId={isTaskSession ? `task-session-icon-${session.id}` : undefined}
            idleIcon={mcpSession ? <McpSessionIndicator sessionId={session.id} /> : undefined}
          />
          <span className="min-w-0 flex-1 truncate">{session.title}</span>
          <span className="absolute right-1 top-1/2 z-10 hidden -translate-y-1/2 items-center pl-3 group-hover:inline-flex">
            <AgentBadge
              agentId={session.agent_id}
              agentName={agentName}
              type="agent"
              size="sm"
            />
          </span>
        </button>
      </TableCell>
      <TableCell role="cell" className="min-w-0 p-0 text-right">
        <time
          data-testid={`session-last-touched-${session.id}`}
          dateTime={session.last_active_at}
          aria-label={`${t("sidebar.lastTouched")}: ${lastTouched}`}
          className="min-w-0 text-right text-xs tabular-nums text-muted-foreground"
        >
          {lastTouched}
        </time>
      </TableCell>
    </TableRow>
  );
});

type AgentConversationsVirtualizedItem =
  | { kind: "session"; row: AgentConversationsTableRowProps }
  | { kind: "footer"; content: ReactNode };

function VirtualizedAgentConversationsTable({ children, style }: TableProps) {
  return (
    <AgentConversationsTable virtualized style={style}>
      {children}
    </AgentConversationsTable>
  );
}

function VirtualizedAgentConversationsTableRow({
  item,
  children: _children,
  ...tableRowProps
}: ItemProps<AgentConversationsVirtualizedItem>) {
  if (item.kind === "footer") {
    return (
      <TableRow {...tableRowProps} className="border-b-0 hover:bg-transparent">
        <TableCell colSpan={2} className="p-0">
          {item.content}
        </TableCell>
      </TableRow>
    );
  }

  return <AgentConversationsTableRow {...item.row} {...tableRowProps} />;
}

const virtualizedAgentConversationsTableComponents: TableComponents<AgentConversationsVirtualizedItem> = {
  Table: VirtualizedAgentConversationsTable,
  TableHead: TableHeader,
  TableBody,
  TableRow: VirtualizedAgentConversationsTableRow,
};

function renderAgentConversationsTableHeader() {
  return <AgentConversationsTableHeaderRow />;
}

function renderVirtualizedAgentConversationsTableItem() {
  return null;
}

export interface AgentConversationsVirtualizedTableProps extends Omit<
  ComponentProps<typeof TableVirtuoso>,
  "components" | "data" | "fixedFooterContent" | "fixedHeaderContent" | "initialItemCount" | "itemContent" | "rows"
> {
  rows: readonly AgentConversationsTableRowProps[];
  footer?: ReactNode;
}

/** A semantic, virtualized variant of AgentConversationsTable. */
export function AgentConversationsVirtualizedTable({
  rows,
  footer,
  className,
  ...virtuosoProps
}: AgentConversationsVirtualizedTableProps) {
  const items = useMemo<AgentConversationsVirtualizedItem[]>(
    () => [
      ...rows.map((row) => ({ kind: "session" as const, row })),
      ...(footer == null ? [] : [{ kind: "footer" as const, content: footer }]),
    ],
    [footer, rows],
  );

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <TableVirtuoso<AgentConversationsVirtualizedItem>
        {...virtuosoProps}
        className={cn("min-h-0 flex-1 overflow-x-hidden", className)}
        data={items}
        initialItemCount={1}
        fixedHeaderContent={renderAgentConversationsTableHeader}
        itemContent={renderVirtualizedAgentConversationsTableItem}
        components={virtualizedAgentConversationsTableComponents}
      />
    </div>
  );
}
